import { cn } from "@/lib/utils"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "./button"
import { DoneIcon } from "@/components/icons"
import { XIcon } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useIsMobile } from "@/hooks/useIsMobile"

interface StepperProps {
  className?: string
  tabs: { 
    code: string, 
    number: string, 
    label: string, 
    checked: boolean, 
    disabled: boolean
  }[]
  activeStep: string
  onStepChange?: (step: string) => void
}

export function Stepper({ className, tabs, activeStep, onStepChange }: StepperProps) {
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  return (
    <header className={cn("shrink-0 pt-2 lg:pt-4 bg-background", className)}>
      <div className="mx-auto max-w-[1920px] px-2 lg:px-4">
        <div className="relative z-[100] flex items-center lg:gap-1 gap-[2px]">
          <Tabs
            className="w-full grow"
            value={activeStep}
            onValueChange={onStepChange}
          >
            <TabsList className="w-full bg-transparent lg:gap-2 gap-[2px] px-0">
              {tabs.map((tab) => {
                const isActive = tab.code === activeStep

                return (
                  <TabsTrigger
                    disabled={tab.disabled}
                    key={tab.code}
                    {...(tab.checked ? { "data-checked": true } : {})}
                    value={tab.code}
                    className={cn(
                      "font-medium flex items-center bg-card text-light-muted rounded-[8px] cursor-pointer",
                      "data-active:bg-card data-active:shadow-none! data-active:text-brand data-active:border-brand",
                      "data-active:[&>.stepper-number]:bg-brand data-active:[&>.stepper-number]:text-white",
                      "hover:text-light-muted data-active:hover:text-brand",
                      "data-checked:text-primary data-checked:hover:text-primary data-checked:bg-muted data-checked:[&>.stepper-number]:text-primary",
                      "disabled:opacity-100 disabled:cursor-not-allowed",
                      !isMobile && "w-full flex-1 px-5 py-[18px] h-15 text-[18px] leading-6 gap-2",
                      isMobile && isActive && "flex-1 min-w-0 h-10 px-4 py-2 text-[12px] leading-6 justify-center",
                      isMobile && !isActive && "size-10 shrink-0 flex-none p-0 justify-center gap-0 text-[12px]",
                    )}
                  >
                    {(!isMobile || !isActive) && (
                      <span className="stepper-number flex shrink-0 items-center justify-center size-7 rounded-[2px]">
                        {tab.checked ? (
                          <DoneIcon size={24} className="size-6" />
                        ) : (
                          <span>{tab.number}</span>
                        )}
                      </span>
                    )}
                    {(!isMobile || isActive) && (
                      <span className="truncate">{tab.label}</span>
                    )}
                  </TabsTrigger>
                )
              })}
            </TabsList>
          </Tabs>
          <Button
            variant="secondary"
            className={cn(
              "shrink-0 bg-muted lg:w-auto",
              "max-lg:size-10 max-lg:min-w-10 max-lg:w-10 max-lg:gap-0 max-lg:p-0 max-lg:justify-center",
              "max-lg:[&>span:nth-child(2)]:hidden",
            )}
            startIcon={<XIcon size={isMobile ? 20 : 24}  className="!size-5 lg:size-6" />}
            endIcon={false}
            aria-label="Скрыть форму"
            onClick={() => { navigate("/") }}
          >
            Скрыть форму
          </Button>
        </div>
      </div>
    </header>
  )
}
